import {
    Button,
    ButtonBase,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme
} from "@mui/material";
import {ReactNode, useState} from "react";
import {useKeycloak} from "@react-keycloak/web";
import './OrlenProgram.scss';
import {CloseIcon, MailIcon, OpenInNewIcon, ZoomInIcon} from "../common/Icons";
import ImageLightbox, {LightboxImage} from "../common/ImageLightbox";
import ContactForm, {ContactFormField} from "../common/ContactForm";
import fwmLogo from './fwm-logo.png';
import programFlyer from './orlen-biznestank.webp';
import thankYouFlyer from './fwm-podziekowanie.webp';
import orlenLogo from '../services/icons/orlen.png';

const CONTACT_EMAIL = "ekonom.konsulty@op.pl";
const FWM_WEBSITE = "https://kwpzm.org/forum/";

const gallery: LightboxImage[] = [
    {src: programFlyer, alt: "Program BiznesTank – ulotka"},
    {src: thankYouFlyer, alt: "Podziękowanie za wsparcie klasztorów klauzurowych"},
];

const discounts = [
    {value: "16 gr", label: "paliwa zwykłe", products: "EFECTA 95 (E10), EFECTA DIESEL, LPG"},
    {value: "20 gr", label: "paliwa premium", products: "VERVA 98, VERVA DIESEL"},
    {value: "10 gr", label: "dodatkowo", products: "ON B100 i ON B20"},
    {value: "5%", label: "dodatkowo", products: "myjnia automatyczna i odkurzacz samoobsługowy"},
];

const institutions = [
    "domy zakonne",
    "parafie",
    "kościelne i zakonne instytucje",
    "prowadzone dzieła",
    "powołane przez podmioty kościelne spółki i jednostki wydzielone (np. domy rekolekcyjne, dzieła edukacyjne " +
    "i charytatywne, medyczne, gospodarstwa)",
];

const individuals = [
    "księża",
    "osoby konsekrowane wszystkich stanów (zakonnicy, zakonnice, członkowie instytutów świeckich, pustelnicy, " +
    "dziewice konsekrowane, wdowy pobłogosławione)",
    "pracujący we wszystkich instytucjach zakonnych i kościelnych",
    "należący do grup duszpasterskich (parafialnych, diecezjalnych, związanych z charyzmatem zakonnym, " +
    "ogólnopolskich wraz z duszpasterstwami grup zawodowych)",
];

const supportTotal = {period: "Łącznie w latach 2015–2025", amount: "1 086 875,62 zł"};

const supportStats = [
    {period: "2024", amount: "227 541,48 zł"},
    {period: "2025", amount: "214 291,99 zł"},
    {period: "I–II kwartał 2026", amount: "90 346,74 zł"},
];

function cardRequestFields(parishAddress: string): ContactFormField[] {
    return [
        {
            name: "consecratedPersonName",
            label: "Imię i nazwisko księdza/osoby konsekrowanej",
            requiredMessage: "Podaj imię i nazwisko księdza/osoby konsekrowanej",
        },
        {
            name: "registrationNumber",
            label: "Numery rejestracyjne pojazdów",
            requiredMessage: "Podaj numer rejestracyjny",
            placeholder: "np. WA 12345, KR 6789A",
            helperText: "Kilka numerów oddziel przecinkami",
            uppercase: true,
        },
        {
            name: "postalAddress",
            label: "Adres pocztowy do wysyłki kart",
            requiredMessage: "Podaj adres do wysyłki kart",
            initialValue: parishAddress,
            multiline: true,
        },
    ];
}

function mailto(subject: string, body: string): string {
    return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function Section(props: {title: string, children: ReactNode, className?: string}) {
    return (
        <section className={props.className ? `OrlenSection ${props.className}` : "OrlenSection"}>
            <Typography variant="h6" component="h3" className="OrlenSectionTitle">{props.title}</Typography>
            {props.children}
        </section>
    )
}

function ExternalLink(props: {href: string, children: ReactNode}) {
    return <a href={props.href} target="_blank" rel="noopener noreferrer">{props.children}</a>;
}

function OrlenProgramDialog(props: {open: boolean, onClose: () => void}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const {keycloak} = useKeycloak();

    const parishName: string = keycloak.tokenParsed?.displayed_name ?? "";
    const parishAddress: string = keycloak.tokenParsed?.displayed_address ?? "";

    const agreementHref = mailto(
        "Umowa BiznesTank – włączenie do grupy zakupowej FWM",
        `Szczęść Boże,\n\nw załączniku przesyłam umowę BiznesTank podpisaną na stacji ORLEN z prośbą o włączenie ` +
        `do grupy zakupowej Forum Współpracy Międzyzakonnej.\n\nNazwa parafii / instytucji: ${parishName}\n\n`
    );

    return (
        <>
            <Dialog
                open={props.open}
                onClose={props.onClose}
                maxWidth="lg"
                fullWidth
                fullScreen={isMobile}
                scroll="paper"
                className="OrlenDialog"
                aria-labelledby="orlen-program-title"
            >
                <DialogTitle id="orlen-program-title" className="OrlenDialogTitle">
                    <img src={orlenLogo} alt="ORLEN" className="OrlenDialogLogo"/>
                    <span>ORLEN BiznesTank z Forum Współpracy Międzyzakonnej</span>
                    <IconButton aria-label="Zamknij" onClick={props.onClose} className="OrlenDialogClose">
                        <CloseIcon/>
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers className="OrlenProgram">
                    <div className="OrlenHero">
                        <div className="OrlenHeroText">
                            <Typography className="OrlenEyebrow">
                                Program BiznesTank – razem z Forum Współpracy Międzyzakonnej
                            </Typography>
                            <Typography variant="h4" component="h2" className="OrlenHeadline">
                                Tankując oszczędzasz <span>i wspierasz klasztory klauzurowe</span>
                            </Typography>
                            <Typography className="OrlenAgreement">
                                W ramach porozumienia pomiędzy Forum Współpracy Międzyzakonnej a ORLEN SA z 2010 roku
                                dla programu BiznesTank, podpisanego przez prezesa Jacka Krawca: <em>„Domy Zakonne,
                                Parafie, Osoby Zakonne i Duchowne, osoby pracujące w instytucjach kościelnych,
                                należące do grup duszpasterskich, mogą dokonywać zakupu paliw (LPG, ON, Pb), produktów
                                i usług oferowanych na stacjach ORLEN z rabatami – oferta obejmuje Program FLOTA oraz
                                BIZNESTANK”</em>.
                            </Typography>
                            <div className="OrlenPartners">
                                <img src={fwmLogo} alt="Forum Współpracy Międzyzakonnej" className="OrlenPartnerFwm"/>
                                <img src={orlenLogo} alt="ORLEN" className="OrlenPartnerOrlen"/>
                            </div>
                        </div>
                        <div className="OrlenGallery">
                            {gallery.map((image, i) =>
                                <ButtonBase key={image.src} className="OrlenThumb" onClick={() => setLightboxIndex(i)}
                                            aria-label={`Powiększ: ${image.alt}`}>
                                    <img src={image.src} alt={image.alt}/>
                                    <span className="OrlenThumbOverlay"><ZoomInIcon fontSize="small"/> Powiększ</span>
                                </ButtonBase>
                            )}
                        </div>
                    </div>

                    <Section title="Aktualne zniżki">
                        <div className="OrlenDiscounts">
                            {discounts.map(d =>
                                <div className="OrlenDiscount" key={d.value}>
                                    <span className="OrlenDiscountValue">{d.value}</span>
                                    <span className="OrlenDiscountLabel">{d.label}</span>
                                    <span className="OrlenDiscountProducts">{d.products}</span>
                                </div>
                            )}
                        </div>
                    </Section>

                    <Section title="Do kogo adresowany jest program?">
                        <div className="OrlenAudience">
                            <div className="OrlenAudienceCard">
                                <Typography className="OrlenAudienceTitle">Instytucje</Typography>
                                <ul className="OrlenList">{institutions.map(i => <li key={i}>{i}</li>)}</ul>
                            </div>
                            <div className="OrlenAudienceCard">
                                <Typography className="OrlenAudienceTitle">Osoby indywidualne</Typography>
                                <ul className="OrlenList">{individuals.map(i => <li key={i}>{i}</li>)}</ul>
                            </div>
                        </div>
                    </Section>

                    <Section title="Jak wspieramy klasztory klauzurowe?">
                        <div className="OrlenSupport">
                            <div className="OrlenSupportText">
                                <div className="OrlenSupportHighlight">
                                    <span className="OrlenSupportHighlightValue">1 gr</span>
                                    <span>z każdego zatankowanego litra paliwa trafia za pośrednictwem FWM
                                        do klasztorów klauzurowych w Polsce</span>
                                </div>
                                <Typography className="OrlenParagraph">
                                    W ramach programu BiznesTank od 2015 roku wspieramy klasztory klauzurowe — obecnie
                                    jest ich <strong>86 (84 żeńskie i 2 męskie)</strong>. Przelewy realizowane są na
                                    konta poszczególnych klasztorów według listy, a po zakończeniu jednego cyklu pomocy
                                    zaczyna się następny.
                                </Typography>
                                <Typography className="OrlenParagraph">
                                    Do wsparcia dokłada się również 1 zł z każdej{" "}
                                    <ExternalLink href="https://kawa-zakonna.pl/">kawy zakonnej</ExternalLink> oraz
                                    wpływy od produktów{" "}
                                    <ExternalLink href="https://dabrowscyczk.pl/">CZK Dąbrowscy</ExternalLink>.
                                </Typography>
                            </div>
                            <div className="OrlenStats">
                                <div className="OrlenStatTotal">
                                    <span className="OrlenStatPeriod">{supportTotal.period}</span>
                                    <span className="OrlenStatAmount">{supportTotal.amount}</span>
                                </div>
                                {supportStats.map(s =>
                                    <div key={s.period} className="OrlenStat">
                                        <span className="OrlenStatPeriod">{s.period}</span>
                                        <span className="OrlenStatAmount">{s.amount}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Section>

                    <Section title="Jak skorzystać?">
                        <div className="OrlenSteps">
                            <div className="OrlenStepsInfo">
                                <div className="OrlenStepCard">
                                    <span className="OrlenStepTag">Karty na NIP Forum</span>
                                    <Typography className="OrlenStepTitle">Zamów karty przez formularz</Typography>
                                    <Typography className="OrlenParagraph">
                                        Podaj w formularzu numery rejestracyjne pojazdów oraz adres pocztowy do wysyłki
                                        kart BiznesTank — karty zostaną wyrobione na NIP Forum Współpracy
                                        Międzyzakonnej. Zgłoszenie możesz też wysłać bezpośrednio na adres{" "}
                                        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                                    </Typography>
                                </div>
                                <div className="OrlenStepCard">
                                    <span className="OrlenStepTag">Faktura na siebie / podmiot kościelny</span>
                                    <Typography className="OrlenStepTitle">Dołącz z własną umową</Typography>
                                    <Typography className="OrlenParagraph">
                                        Do grupy zakupowej FWM można włączyć podmioty instytucjonalne, a także osoby
                                        duchowne i zakonne prowadzące jednoosobową działalność gospodarczą.
                                    </Typography>
                                    <ol className="OrlenStepList">
                                        <li>
                                            Podpisz na swój NIP umowę na program BiznesTank na najbliższej stacji
                                            ORLEN. Od razu możesz wtedy samodzielnie wyrabiać karty na stacji i masz
                                            dostęp do menedżera floty na portalu.
                                        </li>
                                        <li>
                                            Prześlij podpisaną umowę na adres{" "}
                                            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> celem włączenia
                                            do grupy.
                                        </li>
                                    </ol>
                                    <Button variant="outlined" href={agreementHref} startIcon={<MailIcon/>}
                                            className="OrlenSecondaryButton">
                                        Wyślij umowę
                                    </Button>
                                </div>
                            </div>
                            <ContactForm
                                endpoint="/api/v1/orlenContact"
                                title="Zamów karty BiznesTank"
                                lead="Wypełnij zgłoszenie — skontaktujemy się w sprawie wyrobienia kart."
                                fields={cardRequestFields(parishAddress)}
                                messagePlaceholder="Np. liczba kart, rodzaje paliwa, pytania dotyczące programu"
                                submitLabel="Wyślij zgłoszenie"
                                successMessage="Zgłoszenie zostało wysłane. Wkrótce skontaktujemy się w sprawie kart BiznesTank."
                            />
                        </div>
                    </Section>

                    <div className="OrlenCta">
                        <div>
                            <Typography className="OrlenCtaTitle">Przyłącz się do dobrej inicjatywy! Razem możemy więcej!</Typography>
                            <Typography className="OrlenCtaText">
                                Tankując paliwo, przy okazji wspieraj klasztory klauzurowe.
                            </Typography>
                        </div>
                        <Button variant="contained" href={FWM_WEBSITE} target="_blank" rel="noopener noreferrer"
                                endIcon={<OpenInNewIcon/>} className="OrlenCtaButton">
                            Wszystkie propozycje FWM
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            <ImageLightbox images={gallery} index={lightboxIndex} onChange={setLightboxIndex}/>
        </>
    )
}

export default OrlenProgramDialog;
